<template>
  <v-dialog :model-value="group !== null" max-width="520" @update:model-value="(value: boolean) => !value && emit('close')">
    <div v-if="group" class="rounded-xl bg-white p-5">
      <h3 class="m-0 text-base font-semibold text-gray-900">Planning note</h3>
      <p class="m-0 mt-1 text-xs text-gray-400">{{ subject }} · {{ weekLabel }}</p>

      <template v-if="editing">
        <v-textarea
          v-model="draft"
          class="mt-3"
          variant="outlined"
          density="compact"
          rows="5"
          autofocus
          :counter="MAX_NOTES_LENGTH"
          :maxlength="MAX_NOTES_LENGTH"
          :error-messages="clearingAnonymousBlock ? 'A reservation with no PO needs a note — it is all that identifies it.' : []"
          placeholder="Why is this block here, what is it waiting on…"
        />
        <div class="mt-2 flex justify-end gap-2">
          <button class="btn-secondary" type="button" @click="cancelEditing">Cancel</button>
          <button class="btn-primary" type="button" :disabled="!canSave" @click="emit('save', draft.trim())">Save</button>
        </div>
      </template>

      <template v-else>
        <p class="block-note m-0 mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">{{ group.notes }}</p>
        <p v-if="canWrite && isAnonymousReservation" class="m-0 mt-2 text-xs text-gray-400">
          This note is the reservation's only name, so it can be changed but not removed.
        </p>
        <div class="mt-4 flex justify-end gap-2">
          <button class="btn-secondary" type="button" @click="emit('close')">Close</button>
          <button
            v-if="canWrite && !isAnonymousReservation"
            class="btn-danger"
            type="button"
            @click="confirmingDelete = true"
          >
            Delete
          </button>
          <button v-if="canWrite" class="btn-primary" type="button" @click="editing = true">Edit</button>
        </div>
      </template>
    </div>

    <ConfirmDialog
      :open="confirmingDelete"
      title="Delete this note?"
      :message="`The note on ${subject} is removed for good. The block itself stays scheduled.`"
      @confirm="deleteNote"
      @cancel="confirmingDelete = false"
    />
  </v-dialog>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';

  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import { ASSIGNMENT_TYPE, RESERVATION_LABEL } from '@/types/capacityCalendar';
  import type { ScheduleGroup } from '@/utils/scheduleBoard';

  // Mirrors MAX_NOTES_LENGTH on the backend's assignment entity, which rejects anything longer.
  const MAX_NOTES_LENGTH = 500;

  const props = defineProps<{ group: ScheduleGroup | null; weekLabel: string; canWrite?: boolean }>();
  // Removal is a save of an empty note: the endpoint treats blank text as "clear it", so there
  // is one write path and one toast rather than two that could drift.
  const emit = defineEmits<{ close: []; save: [notes: string] }>();

  const draft = ref('');
  const editing = ref(false);
  const confirmingDelete = ref(false);

  // Deliberately not groupLabel: a reservation's label is the note itself, and a heading that
  // repeats the paragraph beneath it says nothing.
  const subject = computed(() => props.group?.poNumber ?? RESERVATION_LABEL);

  // Nothing but the note identifies such a block, so the backend refuses to clear it. Surfaced
  // here so the reason is visible rather than arriving as a 400.
  const isAnonymousReservation = computed(() => props.group?.type === ASSIGNMENT_TYPE.manual && !props.group.poNumber);

  const clearingAnonymousBlock = computed(() => isAnonymousReservation.value && !draft.value.trim());

  const canSave = computed(() => !clearingAnonymousBlock.value && draft.value.trim() !== (props.group?.notes ?? '').trim());

  // Reseeded per open, so reopening after a Cancel does not resurrect the abandoned draft. A
  // block with no note yet has nothing to read, so Add note lands straight in the editor.
  watch(
    () => props.group,
    (group) => {
      draft.value = group?.notes ?? '';
      editing.value = Boolean(group) && !group?.notes?.trim();
      confirmingDelete.value = false;
    },
    { immediate: true },
  );

  function cancelEditing(): void {
    if (!props.group?.notes?.trim()) {
      emit('close');
      return;
    }
    draft.value = props.group.notes;
    editing.value = false;
  }

  function deleteNote(): void {
    confirmingDelete.value = false;
    emit('save', '');
  }
</script>

<style scoped>
  /* Notes run to 500 characters, and are typed as prose with line breaks. */
  .block-note {
    max-height: 50vh;
    overflow-wrap: anywhere;
    overflow-y: auto;
    white-space: pre-wrap;
  }
</style>
